import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Users, Globe, MonitorPlay, FlaskConical, Archive, RotateCcw, Trash2 } from 'lucide-react';
import HardDeletePartnerDialog from '../../components/HardDeletePartnerDialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [deletedPartners, setDeletedPartners] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null); // { id, name, email, client_count, ... }
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');
  const [hardDeleting, setHardDeleting] = useState(false);
  const [hardDeleteResult, setHardDeleteResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [isLoading, setIsLoading] = useState(true);
  const [togglingDemo, setTogglingDemo] = useState(null); // Track which partner is being toggled

  // Fetch partners (active)
  const fetchPartners = async (search = '') => {
    setIsLoading(true);
    try {
      const params = search ? { search } : {};
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/partners`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setPartners(response.data.partners);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch deleted partners
  const fetchDeletedPartners = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/partners/deleted`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeletedPartners(response.data.partners || []);
    } catch (error) {
      console.error('Failed to fetch deleted partners:', error);
      toast.error('Failed to load deleted partners');
    }
  };

  // Restore a deleted partner
  const handleRestore = async (partnerId, partnerName) => {
    if (!window.confirm(`Restore "${partnerName}" and re-link all their clients?`)) return;
    setRestoringId(partnerId);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${API}/partners/${partnerId}/restore`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data?.message || 'Partner restored');
      await fetchDeletedPartners();
      await fetchPartners(searchQuery);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore partner');
    } finally {
      setRestoringId(null);
    }
  };

  // Open the hard-delete confirmation dialog (works for both active + deleted)
  const openHardDelete = (partner) => {
    setHardDeleteTarget(partner);
    setHardDeleteConfirmText('');
    setHardDeleteResult(null);
  };

  // Execute the hard delete
  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    const expected = (hardDeleteTarget.email || '').trim().toLowerCase();
    if (hardDeleteConfirmText.trim().toLowerCase() !== expected) {
      toast.error(`Type "${hardDeleteTarget.email}" exactly to confirm`);
      return;
    }
    setHardDeleting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${API}/partners/${hardDeleteTarget.id}/hard-delete`,
        { confirm_email: hardDeleteConfirmText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHardDeleteResult(res.data);
      toast.success('Partner and all related data permanently deleted');
      await fetchDeletedPartners();
      await fetchPartners(searchQuery);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to hard-delete partner');
    } finally {
      setHardDeleting(false);
    }
  };

  const closeHardDelete = () => {
    setHardDeleteTarget(null);
    setHardDeleteConfirmText('');
    setHardDeleteResult(null);
  };

  // Toggle demo status for a partner
  const toggleDemoStatus = async (partnerId, currentStatus) => {
    setTogglingDemo(partnerId);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/partners/${partnerId}/demo`,
        { is_demo: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Demo mode ${!currentStatus ? 'enabled' : 'disabled'} for partner`);
      // Refresh partners list
      fetchPartners(searchQuery);
    } catch (error) {
      console.error('Failed to toggle demo status:', error);
      toast.error('Failed to update demo status');
    } finally {
      setTogglingDemo(null);
    }
  };

  // Create demo partner
  const createDemoPartner = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/admin/create-demo-partner`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchPartners(searchQuery);
    } catch (error) {
      console.error('Failed to create demo partner:', error);
      toast.error('Failed to create demo partner');
    }
  };

  useEffect(() => {
    fetchPartners();
    fetchDeletedPartners();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchPartners(value);
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="w-4 h-4 ml-1 text-amber-400" />
    ) : (
      <ArrowDown className="w-4 h-4 ml-1 text-amber-400" />
    );
  };

  // Sort partners
  const sortedPartners = useMemo(() => {
    const sorted = [...partners].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Handle date sorting
      if (sortConfig.key === 'last_login' || sortConfig.key === 'created_at') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    return sorted;
  }, [partners, sortConfig]);

  // Format date to DDMMMYY
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear().toString().slice(-2);
    return `${day}${month}${year}`;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '—';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Table columns configuration
  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'last_login', label: 'Last Login', sortable: true },
    { key: 'clients_count', label: 'No of Clients', sortable: true },
    { key: 'iframe_status', label: 'Iframe', sortable: false },
    { key: 'total_amount_generated', label: 'Total Amount Generated', sortable: true },
    { key: 'wills', label: 'Wills Downloaded / Total', sortable: true },
    { key: 'demo', label: 'Demo Mode', sortable: false },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Partners</h1>
          <p className="text-slate-400 mt-1">Detailed overview of all partners and their performance</p>
        </div>
        <div className="flex items-center gap-3">
          {deletedPartners.length > 0 && (
            <Button
              onClick={() => setShowDeleted((v) => !v)}
              variant="outline"
              className={`border-slate-700 ${showDeleted ? 'bg-red-500/10 text-red-400 border-red-500/40' : 'text-slate-300'} hover:bg-slate-800`}
              data-testid="toggle-deleted-partners-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              {showDeleted ? 'Hide' : 'Show'} deleted ({deletedPartners.length})
            </Button>
          )}
          <Button
            onClick={createDemoPartner}
            className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
            data-testid="create-demo-partner-btn"
          >
            <FlaskConical className="w-4 h-4" />
            Initialize Demo Partner
          </Button>
        </div>
      </div>

      {/* Deleted Partners Section */}
      {showDeleted && deletedPartners.length > 0 && (
        <Card className="bg-slate-900/50 border-red-500/30" data-testid="deleted-partners-card">
          <CardHeader className="border-b border-slate-800">
            <CardTitle className="text-white flex items-center gap-2">
              <Archive className="w-5 h-5 text-red-400" />
              Deleted Partners
              <span className="text-sm font-normal text-slate-400">({deletedPartners.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left p-4 text-slate-400 font-medium">Name</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Email</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Deleted On</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Orphaned Clients</th>
                    <th className="text-right p-4 text-slate-400 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedPartners.map((p) => (
                    <tr key={p.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`deleted-partner-row-${p.id}`}>
                      <td className="p-4 text-white font-medium">{p.name}</td>
                      <td className="p-4 text-slate-400 text-sm">{p.email}</td>
                      <td className="p-4 text-slate-400 text-sm">{formatDate(p.deleted_at)}</td>
                      <td className="p-4 text-slate-300 text-sm">{p.client_count ?? 0}</td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleRestore(p.id, p.name)}
                            disabled={restoringId === p.id}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white"
                            data-testid={`restore-partner-btn-${p.id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                            {restoringId === p.id ? 'Restoring...' : 'Restore'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openHardDelete(p)}
                            className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                            data-testid={`hard-delete-btn-${p.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                            Delete Permanently
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Partners Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-white">All Partners</CardTitle>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-9 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
            </div>
          ) : sortedPartners.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-slate-400 text-lg">No partners found</p>
              <p className="text-slate-500 text-sm mt-1">
                {searchQuery ? 'Try a different search term' : 'Add partners from the Dashboard'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className={`text-left p-4 text-slate-400 font-medium ${
                          column.sortable ? 'cursor-pointer hover:text-white transition-colors select-none' : ''
                        }`}
                        onClick={() => column.sortable && handleSort(column.key === 'wills' ? 'wills_downloaded' : column.key)}
                      >
                        <div className="flex items-center">
                          {column.label}
                          {column.sortable && getSortIcon(column.key === 'wills' ? 'wills_downloaded' : column.key)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedPartners.map((partner) => (
                    <tr
                      key={partner.id}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                    >
                      {/* Name */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                            <span className="text-amber-400 font-semibold">
                              {partner.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium">{partner.name}</p>
                              {partner.is_demo && (
                                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-medium rounded-full flex items-center gap-1">
                                  <FlaskConical className="w-3 h-3" />
                                  Demo
                                </span>
                              )}
                            </div>
                            <p className="text-slate-500 text-sm">{partner.email}</p>
                          </div>
                        </div>
                      </td>

                      {/* Last Login */}
                      <td className="p-4">
                        <span className={`text-sm ${partner.last_login ? 'text-slate-300' : 'text-slate-500'}`}>
                          {formatDate(partner.last_login)}
                        </span>
                      </td>

                      {/* No of Clients */}
                      <td className="p-4">
                        <span className={`text-sm font-medium ${partner.clients_count > 0 ? 'text-white' : 'text-slate-500'}`}>
                          {partner.clients_count > 0 ? partner.clients_count : '—'}
                        </span>
                      </td>

                      {/* Iframe Status */}
                      <td className="p-4">
                        {partner.embed_domain ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                              <MonitorPlay className="w-3.5 h-3.5 text-emerald-400" />
                            </div>
                            <span className="text-emerald-400 text-sm font-medium">Enabled</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-slate-700/50 flex items-center justify-center">
                              <Globe className="w-3.5 h-3.5 text-slate-500" />
                            </div>
                            <span className="text-slate-500 text-sm">Subdomain</span>
                          </div>
                        )}
                      </td>

                      {/* Total Amount Generated */}
                      <td className="p-4">
                        <span className={`text-sm font-medium ${partner.total_amount_generated > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                          {formatCurrency(partner.total_amount_generated)}
                        </span>
                      </td>

                      {/* Wills Downloaded / Total */}
                      <td className="p-4">
                        {partner.total_wills > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{partner.wills_downloaded}</span>
                            <span className="text-slate-500">/</span>
                            <span className="text-slate-400">{partner.total_wills}</span>
                            <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden ml-2">
                              <div
                                className="h-full bg-amber-500 rounded-full transition-all duration-300"
                                style={{
                                  width: `${(partner.wills_downloaded / partner.total_wills) * 100}%`,
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-sm">—</span>
                        )}
                      </td>

                      {/* Demo Mode Toggle */}
                      <td className="p-4">
                        <button
                          onClick={() => toggleDemoStatus(partner.id, partner.is_demo)}
                          disabled={togglingDemo === partner.id}
                          className={`
                            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                            ${partner.is_demo ? 'bg-purple-600' : 'bg-slate-700'}
                            ${togglingDemo === partner.id ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:opacity-80'}
                          `}
                          data-testid={`demo-toggle-${partner.id}`}
                        >
                          <span
                            className={`
                              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                              ${partner.is_demo ? 'translate-x-6' : 'translate-x-1'}
                            `}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {sortedPartners.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <p className="text-slate-400 text-sm">Total Partners</p>
            <p className="text-2xl font-bold text-white mt-1">{sortedPartners.length}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <p className="text-slate-400 text-sm">Total Clients (All Partners)</p>
            <p className="text-2xl font-bold text-white mt-1">
              {sortedPartners.reduce((sum, p) => sum + (p.clients_count || 0), 0) || '—'}
            </p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <p className="text-slate-400 text-sm">Total Revenue (All Partners)</p>
            <p className="text-2xl font-bold text-emerald-400 mt-1">
              {formatCurrency(sortedPartners.reduce((sum, p) => sum + (p.total_amount_generated || 0), 0))}
            </p>
          </div>
        </div>
      )}

      {/* Hard-Delete Partner Dialog */}
      <HardDeletePartnerDialog
        target={hardDeleteTarget}
        confirmText={hardDeleteConfirmText}
        setConfirmText={setHardDeleteConfirmText}
        onClose={closeHardDelete}
        onConfirm={handleHardDelete}
        isDeleting={hardDeleting}
        result={hardDeleteResult}
      />
    </div>
  );
};

export default Partners;
